// Hall of Fame - League Winners Gallery
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLeague } from '../contexts/LeagueContext';
import { Button, Card, Input } from '../components';
import HamburgerMenu from '../components/HamburgerMenu';
import { getWinnersForLeague, createWinnerRecord, updateWinnerRecord, deleteWinnerRecord } from '../services/winnersService';
import { uploadLeagueWinnerPhoto, deleteWinnerPhoto } from '../services/storageService';

const HallOfFame = () => {
  const navigate = useNavigate();
  const { leagueId } = useParams();
  const { currentLeague, isLeagueAdmin, loadLeague } = useLeague();

  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Add/Edit form state
  const [showForm, setShowForm] = useState(false);
  const [editingWinnerId, setEditingWinnerId] = useState(null);
  const [formData, setFormData] = useState({
    competitionName: '',
    year: new Date().getFullYear(),
    winnerName: '',
    winnerType: 'overall',
    categoryName: '',
    notes: ''
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Filter state
  const [filterType, setFilterType] = useState('all'); // 'all', 'overall', 'category'

  useEffect(() => {
    if (leagueId) {
      loadLeague(leagueId);
      loadWinners();
    }
  }, [leagueId]);

  const loadWinners = async () => {
    try {
      setLoading(true);
      const winnersData = await getWinnersForLeague(leagueId);
      setWinners(winnersData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading winners:', err);
      setError('Failed to load winners');
      setLoading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setPhotoFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleAddNew = () => {
    setEditingWinnerId(null);
    setFormData({
      competitionName: '',
      year: new Date().getFullYear(),
      winnerName: '',
      winnerType: 'overall',
      categoryName: '',
      notes: ''
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleEdit = (winner) => {
    setEditingWinnerId(winner.id);
    setFormData({
      competitionName: winner.competitionName,
      year: winner.year,
      winnerName: winner.winnerName,
      winnerType: winner.winnerType || 'overall',
      categoryName: winner.categoryName || '',
      notes: winner.notes || ''
    });
    setPhotoFile(null);
    setPhotoPreview(winner.photoUrl);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingWinnerId(null);
    setFormData({
      competitionName: '',
      year: new Date().getFullYear(),
      winnerName: '',
      winnerType: 'overall',
      categoryName: '',
      notes: ''
    });
    setPhotoFile(null);
    setPhotoPreview(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.competitionName.trim()) {
      setError('Competition name is required');
      return;
    }

    if (!formData.winnerName.trim()) {
      setError('Winner name is required');
      return;
    }

    if (!formData.year || formData.year < 1900 || formData.year > 2100) {
      setError('Please enter a valid year');
      return;
    }

    if (formData.winnerType === 'category' && !formData.categoryName.trim()) {
      setError('Category name is required for category winners');
      return;
    }

    setError('');
    setSuccess('');
    setSaving(true);

    try {
      let photoUrl = editingWinnerId ? winners.find(w => w.id === editingWinnerId)?.photoUrl : null;

      // Upload new photo if provided
      if (photoFile) {
        setUploadingPhoto(true);
        photoUrl = await uploadLeagueWinnerPhoto(leagueId, photoFile);
        setUploadingPhoto(false);
      }

      const winnerData = {
        competitionName: formData.competitionName.trim(),
        year: parseInt(formData.year),
        winnerName: formData.winnerName.trim(),
        winnerType: formData.winnerType,
        categoryName: formData.winnerType === 'category' ? formData.categoryName.trim() : null,
        notes: formData.notes.trim() || null,
        photoUrl
      };

      if (editingWinnerId) {
        // Update existing winner
        await updateWinnerRecord(editingWinnerId, winnerData);
        setSuccess('Winner updated successfully!');
      } else {
        // Create new winner
        await createWinnerRecord(leagueId, winnerData);
        setSuccess('Winner added successfully!');
      }

      await loadWinners();
      handleCancel();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving winner:', err);
      setError(err.message || 'Failed to save winner');
    } finally {
      setSaving(false);
      setUploadingPhoto(false);
    }
  };

  const handleDelete = async (winner) => {
    if (!window.confirm(`Are you sure you want to delete ${winner.competitionName} - ${winner.winnerName}?`)) {
      return;
    }

    setError('');
    setSuccess('');

    try {
      // Delete photo if exists
      if (winner.photoUrl) {
        await deleteWinnerPhoto(winner.photoUrl);
      }

      // Delete winner record
      await deleteWinnerRecord(winner.id);

      setSuccess('Winner deleted successfully!');
      await loadWinners();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting winner:', err);
      setError(err.message || 'Failed to delete winner');
    }
  };

  // Filter winners
  const filteredWinners = winners.filter(w => {
    if (filterType === 'all') return true;
    return w.winnerType === filterType;
  });

  // Group winners by year
  const winnersByYear = filteredWinners.reduce((acc, winner) => {
    const year = winner.year || 'Unknown';
    if (!acc[year]) acc[year] = [];
    acc[year].push(winner);
    return acc;
  }, {});

  const sortedYears = Object.keys(winnersByYear).sort((a, b) => b - a);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading Hall of Fame...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <HamburgerMenu />
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 mb-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/league/${leagueId}`)}>
              ← Back to League
            </Button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Hall of Fame</h1>
              <p className="text-sm text-gray-600 mt-1">
                {currentLeague?.name || 'League'} - Historical winners
              </p>
            </div>
            {isLeagueAdmin && !showForm && (
              <Button onClick={handleAddNew}>
                Add Winner
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
            {success}
          </div>
        )}

        {/* Filter Tabs */}
        {!showForm && winners.length > 0 && (
          <div className="flex gap-2 mb-6">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Winners
            </button>
            <button
              onClick={() => setFilterType('overall')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'overall'
                  ? 'bg-yellow-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Overall Champions
            </button>
            <button
              onClick={() => setFilterType('category')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterType === 'category'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Category Winners
            </button>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && isLeagueAdmin && (
          <Card className="mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingWinnerId ? 'Edit Winner' : 'Add New Winner'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Competition Name
                  </label>
                  <Input
                    value={formData.competitionName}
                    onChange={(e) => setFormData({ ...formData, competitionName: e.target.value })}
                    placeholder="e.g., Skei Kamp Kampen"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Year
                  </label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    min="1900"
                    max="2100"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Winner Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="winnerType"
                      value="overall"
                      checked={formData.winnerType === 'overall'}
                      onChange={(e) => setFormData({ ...formData, winnerType: e.target.value })}
                      className="mr-2"
                    />
                    Overall Champion
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="winnerType"
                      value="category"
                      checked={formData.winnerType === 'category'}
                      onChange={(e) => setFormData({ ...formData, winnerType: e.target.value })}
                      className="mr-2"
                    />
                    Category Winner
                  </label>
                </div>
              </div>

              {formData.winnerType === 'category' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category Name
                  </label>
                  <Input
                    value={formData.categoryName}
                    onChange={(e) => setFormData({ ...formData, categoryName: e.target.value })}
                    placeholder="e.g., Poker, Board Games"
                    required={formData.winnerType === 'category'}
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Winner Name
                </label>
                <Input
                  value={formData.winnerName}
                  onChange={(e) => setFormData({ ...formData, winnerName: e.target.value })}
                  placeholder="e.g., John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional information..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Winner Photo (16:9 recommended)
                </label>
                {photoPreview && (
                  <div className="mb-3">
                    <img
                      src={photoPreview}
                      alt="Preview"
                      className="w-full max-w-md h-auto rounded-lg object-cover"
                      style={{ aspectRatio: '16/9' }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handlePhotoChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                <p className="text-xs text-gray-500 mt-1">Max 5MB - JPG, PNG, or WebP</p>
              </div>

              <div className="flex gap-3">
                <Button type="submit" disabled={saving || uploadingPhoto}>
                  {saving ? (uploadingPhoto ? 'Uploading photo...' : 'Saving...') : editingWinnerId ? 'Update Winner' : 'Add Winner'}
                </Button>
                <Button type="button" onClick={handleCancel} variant="outline" disabled={saving}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        )}

        {/* Winners Display */}
        {filteredWinners.length > 0 ? (
          <div className="space-y-8">
            {sortedYears.map(year => (
              <div key={year}>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <span className="text-yellow-500">🏆</span> {year}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {winnersByYear[year].map(winner => (
                    <Card key={winner.id} className="overflow-hidden">
                      {winner.photoUrl && (
                        <div className="mb-4 -mx-6 -mt-6">
                          <img
                            src={winner.photoUrl}
                            alt={`${winner.winnerName} - ${winner.competitionName}`}
                            className="w-full h-auto object-cover"
                            style={{ aspectRatio: '16/9' }}
                          />
                        </div>
                      )}
                      <div>
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{winner.competitionName}</h3>
                            {winner.winnerType === 'category' && winner.categoryName && (
                              <p className="text-sm text-purple-600 font-medium">{winner.categoryName}</p>
                            )}
                          </div>
                          <span className={`text-2xl ${winner.winnerType === 'overall' ? '' : 'grayscale opacity-70'}`}>
                            {winner.winnerType === 'overall' ? '🏆' : '🥇'}
                          </span>
                        </div>
                        <p className="text-lg font-semibold text-blue-600 mb-2">{winner.winnerName}</p>
                        {winner.isAutomatic && (
                          <span className="inline-block text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded mb-2">
                            Auto-recorded
                          </span>
                        )}
                        {winner.notes && (
                          <p className="text-sm text-gray-600 mb-3">{winner.notes}</p>
                        )}
                        {isLeagueAdmin && (
                          <div className="flex gap-2 mt-4">
                            <Button onClick={() => handleEdit(winner)} size="sm" variant="outline">
                              Edit
                            </Button>
                            <Button onClick={() => handleDelete(winner)} size="sm" variant="outline" className="text-red-600 hover:text-red-700 border-red-300">
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card>
            <div className="text-center py-12">
              <span className="text-6xl mb-4 block">🏆</span>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Winners Yet</h3>
              <p className="text-gray-600 mb-4">
                {isLeagueAdmin
                  ? 'Add historical winners to create a Hall of Fame for this league.'
                  : 'The league admin hasn\'t added any historical winners yet.'}
              </p>
              {isLeagueAdmin && (
                <Button onClick={handleAddNew}>
                  Add First Winner
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

export default HallOfFame;
